/**
 * Cart Module - AJAX add-to-cart, remove, coupon.
 * Loaded on: Homepage, Shop, Product, Cart pages.
 */

const config = window.lsbbConfig || {};
// 0.6.92 — button states come translated from LSBB_Asset_Loader (Vladana A5: "Adding.." on DE).
const I18N = Object.assign( { adding: 'Adding...', added: 'Added!' }, config.i18n || {} );

/**
 * Add product to cart via AJAX.
 */
export async function addToCart( productId, quantity = 1, variationId = 0, extraData = {} ) {
	const formData = new FormData();
	formData.append( 'action', 'lsbb_add_to_cart' );
	formData.append( 'product_id', productId );
	formData.append( 'quantity', quantity );
	formData.append( '_wpnonce', config.nonce );

	if ( variationId ) {
		formData.append( 'variation_id', variationId );
	}

	Object.entries( extraData ).forEach( ( [ key, val ] ) => {
		formData.append( key, val );
	} );

	try {
		const res = await fetch( config.ajaxUrl, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin',
		} );

		const data = await res.json();

		if ( data.success ) {
			// Update Alpine cart store. A de-duplicated add returned an existing line
			// rather than adding one, so incrementing would over-count the badge.
			const store = window.lsbb?.Alpine?.store( 'cart' );
			if ( store && ! data.data?.deduped ) {
				store.increment();
			}
			document.dispatchEvent( new CustomEvent( 'lsbb:added-to-cart', {
				detail: { productId, quantity, variationId },
			} ) );
		}

		return data;
	} catch ( err ) {
		console.error( 'Add to cart failed:', err );
		return { success: false, error: err.message };
	}
}

/**
 * Remove item from cart.
 */
export async function removeFromCart( cartItemKey ) {
	const formData = new FormData();
	formData.append( 'action', 'lsbb_remove_from_cart' );
	formData.append( 'cart_item_key', cartItemKey );
	formData.append( '_wpnonce', config.nonce );

	try {
		const res = await fetch( config.ajaxUrl, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin',
		} );

		const data = await res.json();

		if ( data.success ) {
			const store = window.lsbb?.Alpine?.store( 'cart' );
			if ( store ) {
				store.decrement();
			}
			document.dispatchEvent( new CustomEvent( 'lsbb:removed-from-cart', {
				detail: { cartItemKey },
			} ) );
		}

		return data;
	} catch ( err ) {
		console.error( 'Remove from cart failed:', err );
		return { success: false, error: err.message };
	}
}

/**
 * Apply coupon code.
 */
export async function applyCoupon( code ) {
	const formData = new FormData();
	formData.append( 'action', 'lsbb_apply_coupon' );
	formData.append( 'coupon_code', code );
	formData.append( '_wpnonce', config.nonce );

	try {
		const res = await fetch( config.ajaxUrl, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin',
		} );

		return await res.json();
	} catch ( err ) {
		console.error( 'Apply coupon failed:', err );
		return { success: false, error: err.message };
	}
}

// --- Event Delegation for Buy Now / Add-to-Cart buttons ---

document.addEventListener( 'click', async ( e ) => {
	const btn = e.target.closest( '.lsbb-add-to-cart, .add_to_cart_button' );
	if ( ! btn || btn.classList.contains( 'lsbb-loading' ) ) {
		return;
	}

	e.preventDefault();

	const productId = btn.dataset.productId;
	const quantity = btn.dataset.quantity || 1;
	const variationId = btn.dataset.variationId || 0;

	if ( ! productId ) {
		return;
	}

	// Custom price — forwarded with its signature and verified server-side.
	// Editing either of these in devtools produces a mismatch and the add is
	// rejected, so there is nothing to protect on this side.
	const extra = {};
	if ( btn.dataset.customPrice && btn.dataset.cpToken ) {
		extra.custom_price = btn.dataset.customPrice;
		extra.cp_token = btn.dataset.cpToken;
		// Signed alongside the price, so pushing this forward invalidates the token.
		extra.cp_expires = btn.dataset.cpExpires || 0;
	}
	// 0.6.126 — names the companion subscription on the main add, so a feeder bought with it
	// gets its own cart line instead of merging with the same feeder bought without it (bb-cart-sub-upsell 1.3.0).
	if ( btn.dataset.subProductId ) {
		extra.companion_sub = btn.dataset.subProductId;
	}
	// 0.6.155 — the clicked button's sticky-bar picture and the page it is on; lsbb-blocks 3.82.0
	// keeps it as the cart picture on landers (product pages hidden from the catalog) only.
	const pictureHolder = btn.closest( '[data-lsbb-sticky-img]' );
	const pageId = ( document.body.className.match( /\bpostid-(\d+)\b/ ) || [] )[ 1 ];
	if ( pictureHolder && pageId ) {
		extra.lsbb_cart_img = pictureHolder.getAttribute( 'data-lsbb-sticky-img' );
		extra.lsbb_cart_page = pageId;
	}

	// Visual loading state.
	btn.classList.add( 'lsbb-loading' );
	const originalText = btn.textContent;
	btn.textContent = I18N.adding;

	const result = await addToCart( productId, quantity, variationId, extra );

	// Optional companion subscription (bb_add2cart subscription="…" sub_variation="…").
	// Deliberately a SECOND call carrying no custom-price payload: the server refuses a
	// custom price on subscription products, so a shared payload would be rejected.
	// dedupe:1 stops a double-click producing a quantity-2 membership.
	const subProductId = btn.dataset.subProductId;
	if ( result.success && subProductId ) {
		const subResult = await addToCart(
			subProductId,
			1,
			btn.dataset.subVariationId || 0,
			{ dedupe: 1 }
		);
		if ( ! subResult.success ) {
			// By design the main product stays in the cart and the visitor still gets
			// redirected — a missing add-on must not turn into a dead button. Surfaced
			// here and as an event so it is diagnosable rather than silent.
			console.error(
				'[lsbb] companion subscription add failed:',
				subResult?.data?.message || subResult
			);
			document.dispatchEvent( new CustomEvent( 'lsbb:sub-add-failed', {
				detail: { subProductId, variationId: btn.dataset.subVariationId || 0, result: subResult },
			} ) );
		}
	}

	btn.classList.remove( 'lsbb-loading' );

	if ( result.success ) {
		// If the button opts into redirect (e.g. Shop Products "Buy Now"),
		// send the visitor straight to the cart page instead of resetting.
		if ( btn.dataset.redirect ) {
			btn.textContent = I18N.added;
			btn.classList.add( 'lsbb-added' );
			window.location.href = btn.dataset.redirect;
			return;
		}
		btn.textContent = I18N.added;
		btn.classList.add( 'lsbb-added' );
		setTimeout( () => {
			btn.textContent = originalText;
			btn.classList.remove( 'lsbb-added' );
		}, 2000 );
	} else {
		btn.textContent = originalText;
	}
} );

// --- Cart Page: quantity-change auto-submit ---
// Submits the cart form via fetch when the qty dropdown changes, then reloads.
// V2 could swap to wc-ajax=update_cart for fragment-based refresh.
if ( config.pageType === 'cart' ) {
	document.addEventListener( 'change', async ( e ) => {
		const select = e.target.closest( '.lsbb-cart-qty-select' );
		if ( ! select ) {
			return;
		}
		const form = select.closest( 'form.woocommerce-cart-form' );
		if ( ! form ) {
			return;
		}

		document.body.classList.add( 'lsbb-cart-updating' );

		const formData = new FormData( form );
		formData.set( 'update_cart', '1' );

		try {
			await fetch( form.action || window.location.href, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
			} );
			window.location.reload();
		} catch ( err ) {
			console.error( 'Cart qty update failed:', err );
			document.body.classList.remove( 'lsbb-cart-updating' );
		}
	} );
}

// --- Cart Page: "- or -" between checkout actions ---
// 0.6.128 — bug bash C2. The separator used to sit on every child after the first, so an
// express-checkout container that renders nothing (Stripe in desktop Safari with no Apple Pay)
// left a stray "- or -" on the Proceed button. It now goes only on a child that shows something
// and follows another one that does. Express buttons render late, so re-check on resize/mutation.
if ( config.pageType === 'cart' ) {
	// 0.6.129: on live the empty one is PayPal's Pay Later box (.ppcp-messages): a 20px placeholder
	// line, a 0-height iframe and a <style> whose text textContent counted. So: readable text
	// (innerText) or a sized frame/image/button only.
	const CONTENT = 'iframe, img, svg, canvas, video, button, input, a';
	const isShown = ( el ) => {
		if ( el.matches( '.checkout-button' ) ) {
			return true;
		}
		if ( ! el.getClientRects().length ) {
			return false;
		}
		if ( el.innerText.trim() !== '' ) {
			return true;
		}
		return [ ...el.querySelectorAll( CONTENT ) ].some( ( d ) => {
			const r = d.getBoundingClientRect();
			return r.width > 1 && r.height > 1;
		} );
	};

	const markSeparators = ( box ) => {
		let seen = false;
		[ ...box.children ].forEach( ( child ) => {
			const shown = isShown( child );
			child.classList.toggle( 'lsbb-os-sep', shown && seen );
			seen = seen || shown;
		} );
	};

	const watched = new WeakSet();
	const watch = () => {
		document.querySelectorAll( '.lsbb-os-actions' ).forEach( ( box ) => {
			markSeparators( box );
			if ( watched.has( box ) ) {
				return;
			}
			watched.add( box );
			let queued = false;
			const queue = () => {
				if ( queued ) {
					return;
				}
				queued = true;
				// setTimeout, not requestAnimationFrame: rAF never fires in a background tab.
				setTimeout( () => {
					queued = false;
					markSeparators( box );
				}, 50 );
			};
			const sizes = new ResizeObserver( queue );
			[ ...box.children ].forEach( ( c ) => sizes.observe( c ) );
			// Not 'class': our own toggle would re-trigger it.
			new MutationObserver( () => {
				[ ...box.children ].forEach( ( c ) => sizes.observe( c ) );
				queue();
			} ).observe( box, { childList: true, subtree: true, attributes: true, attributeFilter: [ 'style', 'hidden' ] } );
		} );
	};

	watch();
	// WC's cart.js swaps .cart_totals on coupon / shipping updates.
	window.jQuery?.( document.body ).on( 'updated_cart_totals updated_wc_div', watch );
}
